import { CommonModule } from "@angular/common";
import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute, Router, RouterOutlet } from "@angular/router";
import { AuthService } from "./auth/auth.service";
import { GlobalSpinnerComponent } from "./global-spinner.component";
import { Toast } from "./components/utils/toast.component";
import { ToastService } from "./services/toast.service";
import { Subscription } from "rxjs";
import { NavigationHandlerService } from "./services/navigationHandler.service";
import { Title } from "@angular/platform-browser";
import { AppConfig } from "./config/app.config";
import { PeriodicAPICallService } from "./services/periodic-apicall.service";
import { SecureSessionStorageService } from "./services/secure-session-storage.service";
import { LoginDetailsStorageService } from "./services/login-details-storage.service";
import { TempDetailsStorageService } from "./services/temp-details-storage.service";
import { IndicationDetailsStorageService } from "./services/indication-details-storage.service";
import { QuoteDetailsStorageService } from "./services/quote-details-storage.service";
import { PolicyDetailsStorageService } from "./services/policy-details-storage.service";

import { SessionBootstrapService } from "./services/session-bootstrap.service";
import { SessionCacheHydrationService } from "./services/session-cache-hydration.service";
import { VersionCheckService } from "./services/version-check.service";
import { firstValueFrom } from "rxjs";
@Component({
  selector: "app-root",
  standalone: true,
  imports: [RouterOutlet, CommonModule, GlobalSpinnerComponent, Toast],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
})
export class AppComponent implements OnInit, OnDestroy {
  title = "Abacus";
  @ViewChild(Toast) toast!: Toast;
  toastService = inject(ToastService);
  router = inject(Router);
  navigationHandler = inject(NavigationHandlerService);
  activatedRoute = inject(ActivatedRoute);
  periodicApiService = inject(PeriodicAPICallService);
  secureSessionStorage = inject(SecureSessionStorageService);
  loginDetailsStorage = inject(LoginDetailsStorageService);
  tempDetailsStorage = inject(TempDetailsStorageService);
  indicationDetailsStorage = inject(IndicationDetailsStorageService);
  quoteDetailsStorage = inject(QuoteDetailsStorageService);
  policyDetailsStorage = inject(PolicyDetailsStorageService);
  sessionCacheHydration = inject(SessionCacheHydrationService);
  sessionBootstrap = inject(SessionBootstrapService);
  versionCheckService = inject(VersionCheckService);
  titleService = inject(Title);
  toastSubscription = new Subscription();
  pageRefresh:boolean = false;
  tabWasClosed: any;

  constructor(public authService: AuthService) {
    this.setTitle(AppConfig.pageTitle);
    this.toastSubscription = this.toastService.getShowNotification().subscribe({
      next: (action: boolean) => {
        if (action) {
          const { message, type } = this.toastService.getNotificationDetails();
          this.showToast(message, type);
        }
      },
    });
  }

  setTitle(newTitle: string) {
    this.titleService.setTitle(newTitle);
  }

  private async runSessionStartup(): Promise<void> {
    const onAuthPage = this.sessionBootstrap.isAuthRoute(
      this.sessionBootstrap.getBrowserPath()
    );

    if (
      !onAuthPage &&
      this.sessionBootstrap.isActive() &&
      this.authService.hasRestorableSession() &&
      !this.sessionCacheHydration.isHydrated()
    ) {
      try {
        await firstValueFrom(this.sessionCacheHydration.ensureRestored());
      } catch {
        /* routing still completes below */
      }
    }

    this.finishSessionBootstrap(onAuthPage);
  }

  private finishSessionBootstrap(onAuthPage: boolean): void {
    if (!this.sessionBootstrap.isActive()) {
      return;
    }

    if (onAuthPage || !this.authService.hasRestorableSession()) {
      this.sessionBootstrap.completeStartup(0);
      return;
    }

    const intended = this.sessionBootstrap.resolveIntendedRoute();
    if (!this.sessionBootstrap.isWorkflowRoute(intended)) {
      this.sessionBootstrap.completeStartup(0);
      return;
    }

    void this.router
      .navigateByUrl(intended, { replaceUrl: true })
      .then(() => this.sessionBootstrap.finishAfterWorkflowNavigation())
      .catch(() => this.sessionBootstrap.finishAfterWorkflowNavigation());
  }

  // ngOnInit(): void {}

  ngOnInit(): void {
    void this.runSessionStartup();

    this.versionCheckService.checkVersion();

    if (!sessionStorage.getItem('isPageRefreshing')) {
      sessionStorage.setItem('isPageRefreshing', 'false'); 
    }
    
    // if (
    //   typeof localStorage !== "undefined" &&
    //   sessionStorage.getItem("isPageRefreshing") === "true"
    // ) {
    //   sessionStorage.removeItem("isPageRefreshing");
    //   this.pageRefresh = true;
    // }
    // else{
    //   this.pageRefresh = false;
    // }

    const sessionId = Number(this.loginDetailsStorage.getItem('sessionId'));
    const userLATInterval = Number(
      this.loginDetailsStorage.getItem('userLATInterval')
    );
    if (sessionId && userLATInterval && this.authService.getIsOTPVerified()) {
      this.periodicApiService.startPeriodicApiCall();
    }
  }

  showToast(message: string, type: string) {
    this.toast.message = message;
    this.toast.type = type;
    this.toast.show();
    this.toastService.resetNotification(); // reset notification details
  }

/*  
  doBeforeUnload() {
    if (document.visibilityState === 'hidden') {
      this.tabWasClosed = true;
    }

    return false;
  }

  doUnload() {
    if (this.tabWasClosed == 'true') {
      const excludedRoutes = [
        "/login",
        "/forgot-password",
        "/reset-password",
        "/create-password",
      ];
      const currentRoute = this.router.url;
      if (
        excludedRoutes.includes(currentRoute) ){
      const token = Cookies.get("token") ?? "";
      const userId = Number(Cookies.get("userId")) || 0;
      this.authService.logoutOnTabClose(token, userId);
      
     // event.returnValue = "Are you sure you want to leave?";
    }
    }
  }
*/
  // Detects when Page Refresh or Tab Close happens
  /*
  @HostListener("window:beforeunload", ["$event"])
  handleBeforeUnload(event: any): void {
    sessionStorage.setItem("isPageRefreshing", "true");
    const excludedRoutes = [
      "/login",
      "/forgot-password",
      "/reset-password",
      "/create-password",
    ];
    const currentRoute = this.router.url;
    if (
      excludedRoutes.includes(currentRoute) &&
      typeof localStorage !== "undefined"
    ) {
      const token = Cookies.get("token") ?? "";
      const userId = Number(Cookies.get("userId")) || 0;

    if(!this.pageRefresh){
      this.authService.logoutOnTabClose(token, userId);
    }

   

         event.returnValue = "Are you sure you want to leave?";

    }
  }
*/

@HostListener('window:beforeunload', ['$event']) 
beforeUnloadHandler(event: Event) { 
  
  const excludedRoutes = [
    "/login",
    "/forgot-password",
    "/reset-password",
    "/create-password",
  ];
  const currentRoute = this.router.url;
  if (document.visibilityState === 'hidden') {
    this.tabWasClosed = true;
  }
  if (sessionStorage.getItem('isPageRefreshing') === 'false' && excludedRoutes.includes(currentRoute)
) {
      console.log('Tab is closing or navigating away, logging out...');
      this.logoutUser();
      sessionStorage.setItem('isPageRefreshing', 'true');
    }
 
    // Prevent the default behavior (no confirmation dialog, no page reload)
  // if (
  //   excludedRoutes.includes(currentRoute)
  // ) {
  // this.logoutUser(); 
  // }

} 

@HostListener('window:unload', ['$event'])
unloadHandler(event: Event) {
  if (!this.pageRefresh) {
  this.logoutUser(); 
  }
}

logoutUser() {
  const token = this.authService.getToken() ?? "";
  const userId = Number(this.authService.getUserId()) || 0;
  if (token && userId) {
    this.authService.logoutOnTabClose(token, userId, false);
  }
}

  ngOnDestroy(): void {
    this.toastSubscription.unsubscribe();
    this.logoutUser()
  }

}
